import { useCallback, useMemo } from "react";
import type { IAdminAuthService } from "@src/domain/interfaces";
import { AdminAuthLoginService } from "@src/infrastructure/auth/AdminAuthLoginService";

/**
 * Custom hook for admin authentication - Presentation layer
 * Provides a clean interface to admin authentication service
 * Uses singleton pattern to ensure single instance of AdminAuthLoginService
 * Wraps service methods with React-friendly callbacks for stable references
 */
export const useAdminAuth = (): IAdminAuthService => {
    // Get the singleton instance (stable reference)
    const adminAuthService = useMemo(() => AdminAuthLoginService.getInstance(), []);

    // Wrap methods with useCallback for stable references
    const login = useCallback(async (forceRefresh = false) => {
        return adminAuthService.login(forceRefresh);
    }, [adminAuthService]);

    const logout = useCallback(async () => {
        return adminAuthService.logout();
    }, [adminAuthService]);

    const refreshPermissions = useCallback(async () => {
        return adminAuthService.refreshPermissions();
    }, [adminAuthService]);

    const getUser = useCallback(() => {
        return adminAuthService.getUser();
    }, [adminAuthService]);

    const isLoading = useCallback(() => {
        return adminAuthService.isLoading();
    }, [adminAuthService]);

    const getAccessToken = useCallback(async () => {
        return adminAuthService.getAccessToken();
    }, [adminAuthService]);

    const getRefreshToken = useCallback(async () => {
        return adminAuthService.getRefreshToken();
    }, [adminAuthService]);

    // Return stable object reference that implements IAdminAuthService
    return useMemo<IAdminAuthService>(() => ({
        login,
        logout,
        refreshPermissions,
        getUser,
        isLoading,
        getAccessToken,
        getRefreshToken,
    }), [login, logout, refreshPermissions, getUser, isLoading, getAccessToken, getRefreshToken]);
};
